import apiClient from '@/services/apiClient';
import type {
  UserProfile,
  UserSettings,
  TourSeen,
  RegistrationRequest,
  RegistrationResponse,
  OtpSendRequest,
  OtpSendResponse,
  OtpVerifyRequest,
  OtpVerifyResponse,
  OtpResendRequest,
  OtpResendResponse,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
} from '@/types/auth';

// API request and response types
interface LogoutResponse {
  message: string;
}

// LoginRequest/LoginResponse kept for local use

type CheckUserResponse = UserProfile;

// API service function - User registration
async function register(data: RegistrationRequest): Promise<RegistrationResponse> {
  return apiClient.post('/api/v1/users/register', data).then((result) => result.data);
}

// API service function - Password login (Step 1: Verify password and send OTP)
async function passwordLogin(data: LoginRequest): Promise<LoginResponse> {
  return apiClient.post('/api/v1/auth/login', data).then((result) => result.data);
}

// API service function - Verify token validity
async function verifyToken(): Promise<UserProfile> {
  return apiClient.get('/api/v1/auth/verify').then((result) => {
    const { user } = result.data;
    // Transform backend response to match UserProfile interface
    return {
      userId: user.id,
      userName: user.name,
      email: user.email,
      role: user.role || 'user',
    };
  });
}

// API service function - Refresh token
async function refreshToken(): Promise<RefreshTokenResponse> {
  return apiClient.post('/api/v1/auth/refresh').then((result) => result.data);
}

// API service function - Send OTP
async function sendOtp(data: OtpSendRequest): Promise<OtpSendResponse> {
  return apiClient.post('/api/v1/auth/send-otp', data).then((result) => result.data);
}

// API service function - Verify OTP and login
async function verifyOtp(data: OtpVerifyRequest): Promise<OtpVerifyResponse> {
  return apiClient.post('/api/v1/auth/verify-otp', data).then((result) => result.data);
}

// API service function - Resend OTP (uses same endpoint as sendOtp)
async function resendOtp(data: OtpResendRequest): Promise<OtpResendResponse> {
  return apiClient.post('/api/v1/auth/send-otp', data).then((result) => result.data);
}

async function logout(): Promise<LogoutResponse> {
  return apiClient.post('/api/v1/auth/logout').then((result) => result.data);
}

async function checkUser(): Promise<CheckUserResponse> {
  return apiClient.get('/api/v1/auth/verify').then((result) => {
    const { user } = result.data;
    // Transform backend response to match UserProfile interface
    return {
      userId: user.id,
      userName: user.name,
      email: user.email,
      role: user.role || 'user',
    };
  });
}

// API service function - Get user profile (complete profile data)
async function getProfile(): Promise<UserProfile> {
  return apiClient.get('/api/v1/users/profile').then((result) => {
    const user = result.data;
    return {
      userId: user.id,
      userName: user.name,
      email: user.email,
      emailVerified: user.emailVerified,
      status: user.status,
      role: user.role || 'user',
      ...(user.phoneNumber && { phoneNumber: user.phoneNumber }),
      ...(user.avatar && { avatar: user.avatar }),
      ...(user.lastLoginAt && { lastLoginAt: user.lastLoginAt }),
      ...(user.createdAt && { createdAt: user.createdAt }),
      ...(user.updatedAt && { updatedAt: user.updatedAt }),
      ...(user.privacyAccepted !== undefined && { privacyAccepted: user.privacyAccepted }),
      ...(user.privacyAcceptedAt && { privacyAcceptedAt: user.privacyAcceptedAt }),
      ...(user.tourSeen && { tourSeen: user.tourSeen }),
      // settings 明確帶入 null（已 fetch 但無設定），區別於 undefined（尚未 fetch）
      settings: user.settings ?? null,
    };
  });
}

// OAuth login function
async function loginWithGoogle() {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5051';
  window.location.href = `${apiBaseUrl}/api/v1/auth/google`;
}

// Handle OAuth callback and save token
async function handleOAuthCallback(token?: string) {
  if (token) {
    try {
      // Verify if token is valid and get user information
      const response = await apiClient.get('/api/v1/auth/verify', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Token validation failed:', error);
      throw error;
    }
  }
}

// API service function - Forgot password (request reset link)
async function forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
  return apiClient.post('/api/v1/auth/forgot-password', data).then((result) => result.data);
}

// API service function - Reset password (with token from email)
async function resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
  return apiClient.post('/api/v1/auth/reset-password', data).then((result) => result.data);
}

// API service function - Change password (authenticated user)
async function changePassword(data: ChangePasswordRequest): Promise<ChangePasswordResponse> {
  return apiClient.post('/api/v1/users/change-password', data).then((result) => result.data);
}

// API service function - Update user settings (deep partial merge)
async function updateSettings(patch: Partial<UserSettings>): Promise<void> {
  await apiClient.put('/api/v1/users/profile', { settings: patch });
}

// API service function - Accept privacy disclaimer (profile top-level field)
async function acceptPrivacy(): Promise<void> {
  await apiClient.put('/api/v1/users/profile', { privacyAccepted: true });
}

// API service function - Mark preview tours as seen (profile top-level tourSeen JSON)
async function markTourSeen(tourSeen: TourSeen): Promise<void> {
  await apiClient.put('/api/v1/users/profile', { tourSeen });
}

// Export API service object
const authApi = {
  register,
  passwordLogin,
  verifyToken,
  refreshToken,
  sendOtp,
  verifyOtp,
  resendOtp,
  logout,
  checkUser,
  getProfile,
  loginWithGoogle,
  handleOAuthCallback,
  forgotPassword,
  resetPassword,
  changePassword,
  updateSettings,
  acceptPrivacy,
  markTourSeen,
};

export default authApi;
